import { computeVideoObjectFitLayout } from "lib/utils/videoObjectFitLayout";

/** Detection payload from rtsp-infer or NanoOWL /detections (compatible shape). */
export type DetectionPayload = {
    w?: number;
    h?: number;
    boxes?: unknown[];
};

export type DrawDetectionsOptions = {
    fitScreen: boolean;
    bboxColor: string;
    lineWidth?: number;
    /** When false, caller clears the canvas once before multiple overlay passes. */
    clearCanvas?: boolean;
};

/**
 * Draw bounding boxes on a 2D context sized to the display canvas (CSS pixels),
 * mapping from inferencer frame coordinates to video pixels then object-fit.
 */
export function drawDetectionsOnCanvas(
    ctx: CanvasRenderingContext2D,
    canvasWidth: number,
    canvasHeight: number,
    videoWidth: number,
    videoHeight: number,
    det: DetectionPayload | null | undefined,
    options: DrawDetectionsOptions
): void {
    const { fitScreen, bboxColor, lineWidth = 2, clearCanvas = true } = options;
    if (clearCanvas) {
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    }

    const vw = videoWidth;
    const vh = videoHeight;
    if (!det?.boxes || det.boxes.length === 0 || !vw || !vh) {
        return;
    }

    const layout = computeVideoObjectFitLayout(canvasWidth, canvasHeight, vw, vh, fitScreen);
    if (!layout) {
        return;
    }

    const dw = typeof det.w === "number" && det.w > 0 ? det.w : vw;
    const dh = typeof det.h === "number" && det.h > 0 ? det.h : vh;
    const detToVideoX = vw / dw;
    const detToVideoY = vh / dh;

    const { scale, offsetX: ox, offsetY: oy } = layout;

    ctx.strokeStyle = bboxColor;
    ctx.lineWidth = lineWidth;
    ctx.font =
        '12px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';

    for (const b of det.boxes) {
        let x1: number;
        let y1: number;
        let x2: number;
        let y2: number;
        let labelText = "";
        let strokeColor = bboxColor;
        if (
            b &&
            typeof b === "object" &&
            !Array.isArray(b) &&
            Array.isArray((b as { xyxy?: unknown }).xyxy) &&
            (b as { xyxy: unknown[] }).xyxy.length >= 4
        ) {
            const o = b as {
                xyxy: number[];
                label?: string;
                display_label?: string;
                cls?: number;
                conf?: number;
                color?: string;
            };
            [x1, y1, x2, y2] = o.xyxy;
            if (typeof o.color === "string" && /^#[0-9a-fA-F]{6}$/.test(o.color)) {
                strokeColor = o.color;
            }
            const name =
                typeof o.display_label === "string"
                    ? o.display_label
                    : typeof o.label === "string"
                      ? o.label
                      : `cls ${o.cls}`;
            if (typeof o.conf === "number") {
                labelText = `${name} ${Math.round(o.conf * 100)}%`;
            } else {
                labelText = name;
            }
        } else if (Array.isArray(b) && b.length >= 4) {
            [x1, y1, x2, y2] = b as [number, number, number, number];
            if (b.length >= 7 && typeof b[6] === "string") {
                labelText =
                    typeof b[4] === "number"
                        ? `${b[6]} ${Math.round((b[4] as number) * 100)}%`
                        : b[6];
            } else if (b.length >= 6) {
                labelText =
                    typeof b[4] === "number"
                        ? `cls ${b[5]} ${Math.round((b[4] as number) * 100)}%`
                        : `cls ${b[5]}`;
            }
        } else {
            continue;
        }

        const vx1 = x1 * detToVideoX;
        const vy1 = y1 * detToVideoY;
        const vx2 = x2 * detToVideoX;
        const vy2 = y2 * detToVideoY;
        const sx1 = vx1 * scale + ox;
        const sy1 = vy1 * scale + oy;
        const sw = (vx2 - vx1) * scale;
        const sh = (vy2 - vy1) * scale;
        ctx.strokeStyle = strokeColor;
        ctx.strokeRect(sx1, sy1, sw, sh);

        if (labelText) {
            const pad = 4;
            const tw = ctx.measureText(labelText).width;
            const th = 14;
            const bx = sx1;
            const by = Math.max(0, sy1 - th - pad * 2);
            ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
            ctx.fillRect(bx, by, tw + pad * 2, th + pad * 2);
            ctx.fillStyle = strokeColor;
            ctx.fillText(labelText, bx + pad, by + th + pad - 2);
        }
    }
}
